import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, getNestedTranslation } from '../../translations';

// Supported languages with their native names
// Ordered alphabetically by English name for neutrality
export const LANGUAGES = {
  ar: { name: 'Arabic', nativeName: 'العربية' },
  bn: { name: 'Bengali', nativeName: 'বাংলা' },
  zh: { name: 'Chinese', nativeName: '中文' },
  hr: { name: 'Croatian', nativeName: 'Hrvatski' },
  cs: { name: 'Czech', nativeName: 'Čeština' },
  nl: { name: 'Dutch', nativeName: 'Nederlands' },
  en: { name: 'English', nativeName: 'English' },
  fr: { name: 'French', nativeName: 'Français' },
  de: { name: 'German', nativeName: 'Deutsch' },
  fa: { name: 'Farsi', nativeName: 'فارسی' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी' },
  id: { name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  it: { name: 'Italian', nativeName: 'Italiano' },
  ja: { name: 'Japanese', nativeName: '日本語' },
  ko: { name: 'Korean', nativeName: '한국어' },
  ms: { name: 'Malay', nativeName: 'Bahasa Melayu' },
  pl: { name: 'Polish', nativeName: 'Polski' },
  pt: { name: 'Portuguese', nativeName: 'Português' },
  ru: { name: 'Russian', nativeName: 'Русский' },
  es: { name: 'Spanish', nativeName: 'Español' },
  sv: { name: 'Swedish', nativeName: 'Svenska' },
  th: { name: 'Thai', nativeName: 'ภาษาไทย' },
  tr: { name: 'Turkish', nativeName: 'Türkçe' },
  vi: { name: 'Vietnamese', nativeName: 'Tiếng Việt' }
} as const;

export type LanguageCode = keyof typeof LANGUAGES;

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const savedLanguage = localStorage.getItem('zodiac-language');
    // Check if the saved language has translations available
    if (savedLanguage && savedLanguage in translations) {
      return savedLanguage as LanguageCode;
    }
    return 'en';
  });

  const setLanguage = (lang: LanguageCode) => {
    setLanguageState(lang);
  };

  // Translation function - retrieves translation for a given key path
  // Example usage: t('settings.header') returns "Settings" in English
  // Example with params: t('gameInfo.selecting', { piece: 'Goat' }) returns "Selecting Goat"
  const t = (key: string, params?: Record<string, string>): string => {
    // Get the current language's translations
    const currentTranslations = translations[language as keyof typeof translations];

    // If the language doesn't have translations yet, fall back to English
    if (!currentTranslations) {
      return replaceParams(getNestedTranslation(translations.en, key), params);
    }

    // Get the translation for the key
    let translation = getNestedTranslation(currentTranslations, key);

    // If translation not found in current language, fall back to English
    if (translation === key && language !== 'en') {
      translation = getNestedTranslation(translations.en, key);
    }

    return replaceParams(translation, params);
  };

  // Helper to replace {param} placeholders with actual values
  const replaceParams = (text: string, params?: Record<string, string>): string => {
    if (!params) return text;

    let result = text;
    for (const [key, value] of Object.entries(params)) {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    }
    return result;
  };

  useEffect(() => {
    localStorage.setItem('zodiac-language', language);
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Export helper functions for use outside React components
export function getCurrentLanguage(): LanguageCode {
  // Try to get from localStorage, fallback to 'en'
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('zodiac-language') as LanguageCode | null;
    if (stored && ['ar', 'bn', 'zh', 'hr', 'cs', 'nl', 'en', 'fr', 'de', 'fa', 'hi', 'id', 'it', 'ja', 'ko', 'ms', 'pl', 'pt', 'ru', 'es', 'sv', 'th', 'tr', 'vi'].includes(stored)) {
      return stored;
    }
  }
  return 'en';
}

export function getTranslation(lang: LanguageCode, key: string, params?: Record<string, string>): string {
  const currentTranslations = translations[lang as keyof typeof translations];
  if (!currentTranslations) {
    return getNestedTranslation(translations.en, key);
  }

  let translation = getNestedTranslation(currentTranslations, key);
  if (translation === key && lang !== 'en') {
    translation = getNestedTranslation(translations.en, key);
  }

  // Replace params
  if (params) {
    for (const [paramKey, value] of Object.entries(params)) {
      translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), value);
    }
  }

  return translation;
}
